import { Box } from "@mui/material"
import React, { ReactNode, useEffect, useState } from "react"
import LiveIndicator from "src/components/common/live_indicator.gif"
import {
  PlaybackContent,
  playerStore,
  usePlayList,
} from "src/store/PlayerStore"

import useTranslate from "../../../hooks/use-translate"
import { useCredits } from "../../../screens/Detail/components/InfoModal/common"
import { s } from "../../../utilities/common"
import { theme, TI } from "../../../utilities/theme"
import { DGText } from "../../common/DGText"

const LiveLabel = () => {
  const t = useTranslate()
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: s("48px"),
        ml: s("-35px"),
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: s("34px"),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: s("34px") }} component={"img"} src={LiveIndicator} />
      </Box>
      <DGText
        tx={TI.Subheadline01tv}
        sx={{
          color: theme.palette.tertiary.accentYellow,
        }}
      >
        {t("search__results_slider_upcoming_live")}
      </DGText>
    </Box>
  )
}

const SmallLabel = ({ children }: { children: ReactNode }) => {
  return (
    <DGText
      tx={TI.Subheadline05tv}
      sx={{
        color: theme.palette.new.surface["100"],
      }}
    >
      {children}
    </DGText>
  )
}

const BigLabel = ({ children }: { children: ReactNode }) => {
  return (
    <DGText
      tx={TI.H05tv}
      sx={{
        pt: s("8px"),
        pb: s("8px"),
        color: theme.palette.white.main,
      }}
    >
      {children}
    </DGText>
  )
}

export const useTrackIndex = (
  content: PlaybackContent | undefined,
  trackId: string | undefined,
) => {
  const [result, setResult] = useState<{
    listIndex: number
    trackIndex: number
    relativeIndex: number
    total: number
  }>({ listIndex: 0, trackIndex: 0, relativeIndex: 0, total: 0 })

  useEffect(() => {
    if (content?.__typename === "VodConcert") {
      const index =
        content.performanceWorks?.findIndex((pf) => pf.id === trackId) ?? 0
      setResult({
        listIndex: 0,
        trackIndex: index,
        relativeIndex: index,
        total: content.performanceWorks.length,
      })
    } else if (content?.__typename === "Album" && trackId) {
      const listIndex =
        content.trackSets?.findIndex((ts) =>
          ts.tracks.map((t) => t.id).includes(trackId),
        ) ?? 0
      const trackIndex =
        content.trackSets[listIndex]?.tracks?.findIndex(
          (t) => t.id === trackId,
        ) ?? 0
      setResult({
        listIndex,
        trackIndex,
        relativeIndex: content.trackSets.reduce(
          (accumulator, currentValue, i) => {
            return i < listIndex
              ? accumulator + currentValue.tracks.length
              : i === listIndex
                ? accumulator + trackIndex + 1
                : accumulator
          },
          0,
        ),
        total: content.trackSets.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.tracks.length
        }, 0),
      })
    } else if (content?.__typename === "EditorialPlaylist" && trackId) {
      const listIndex =
        content.sections?.findIndex((ts) =>
          ts.items?.map((t) => t.id).includes(trackId),
        ) ?? 0
      const trackIndex =
        content.sections[listIndex]?.items?.findIndex(
          (t) => t.id === trackId,
        ) ?? 0
      setResult({
        listIndex,
        trackIndex,
        relativeIndex: content.sections.reduce(
          (accumulator, currentValue, i) => {
            return i < listIndex
              ? accumulator + currentValue.items.length
              : i === listIndex
                ? accumulator + trackIndex + 1
                : accumulator
          },
          0,
        ),
        total: content.sections.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.items.length
        }, 0),
      })
    }
  }, [content, trackId])
  return result
}

export const PlayerLabel = () => {
  const content = playerStore((state) => state.content)
  const trackId = playerStore((state) => state.trackId)
  const t = useTranslate()
  const { listIndex, trackIndex, relativeIndex, total } = useTrackIndex(
    content,
    trackId,
  )
  const playlist = usePlayList(content)
  const credits = useCredits(content, false)
  const isPreview = playerStore((state) => state.isPreview)

  if (!content || isPreview) return null
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        pb: s("40px"),
      }}
    >
      {content.__typename === "LiveConcert" && (
        <>
          <LiveLabel />
          <BigLabel>{content.title}</BigLabel>
          {credits && credits.length > 0 && (
            <SmallLabel>{credits[0].values.join(", ")}</SmallLabel>
          )}
        </>
      )}
      {content.__typename === "Video" && (
        <>
          <BigLabel>{content.title}</BigLabel>
          {content.subtitle && <SmallLabel>{content.subtitle}</SmallLabel>}
        </>
      )}
      {content.__typename === "VodConcert" &&
        playlist &&
        playlist[listIndex].items[trackIndex] && (
          <>
            <SmallLabel>
              {playlist[listIndex].items[trackIndex].composer}
            </SmallLabel>
            <BigLabel>{playlist[listIndex].items[trackIndex].title}</BigLabel>
            {/* padding */}
            <Box sx={{ width: 100, height: s("16px") }}></Box>
            <SmallLabel>
              {t("player__work_number_subtitle", [relativeIndex + 1, total])}
            </SmallLabel>
          </>
        )}
      {content.__typename === "Album" &&
        playlist &&
        playlist[listIndex].items[trackIndex] && (
          <>
            {content.artistAndGroupDisplayInfo && (
              <SmallLabel>{content.artistAndGroupDisplayInfo}</SmallLabel>
            )}
            <BigLabel>{playlist[listIndex].items[trackIndex].title}</BigLabel>
            <SmallLabel>
              {t("player__track_number_subtitle", [relativeIndex, total])}
            </SmallLabel>
          </>
        )}
      {content.__typename === "EditorialPlaylist" &&
        playlist &&
        playlist[listIndex].items[trackIndex] && (
          <>
            {playlist[listIndex].items[trackIndex].subtitle && (
              <SmallLabel>
                {playlist[listIndex].items[trackIndex].subtitle}
              </SmallLabel>
            )}
            <BigLabel>{playlist[listIndex].items[trackIndex].title}</BigLabel>
            <SmallLabel>
              {t("player__track_number_subtitle", [relativeIndex, total])}
            </SmallLabel>
          </>
        )}
    </Box>
  )
}
